import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Typography, Tabs } from 'antd';
import { 
  TrophyOutlined, 
  TeamOutlined, 
  BarChartOutlined, 
  CalendarOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { auth } from '../firebase';
import { API_BASE_URL, getAuthHeaders } from '../api';
import { consumeDashboardPrefetch } from '../prefetchCache';
import SingaporeSummary from '../components/SingaporeSummary';
import DatabaseStatsTab from '../components/DatabaseStatsTab';
import AnalyticsCharts from '../components/charts/AnalyticsCharts';
import type { DatabaseStats, SingaporeStats, Country, Discipline } from '../types';
import type { Competition } from '../types';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [singaporeStats, setSingaporeStats] = useState<SingaporeStats | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobile, setIsMobile] = useState(false);
  // Prefetched for Database tab (so it's ready when user switches)
  const [dbPrefetch, setDbPrefetch] = useState<{
    stats: DatabaseStats;
    countries: Country[];
    disciplines: Discipline[];
    competitions: Competition[];
  } | null>(null);

  // Check window size for mobile responsiveness
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          await user.getIdToken();
          const prefetched = consumeDashboardPrefetch();
          if (prefetched) {
            prefetched
              .then((data) => {
                if (data.stats != null) setStats(data.stats);
                setSingaporeStats((prev) => ({
                  ...prev,
                  ...(data.majorGamesBenchmarks
                    ? { major_games_benchmarks: data.majorGamesBenchmarks }
                    : {}),
                } as SingaporeStats));
                setOverviewLoading(false);
                setSummaryLoading(false);
              })
              .catch(() => {
                fetchStats();
                fetchMajorGamesBenchmarks();
              });
          } else {
            fetchStats();
            fetchMajorGamesBenchmarks();
          }
        } catch (error) {
          console.error('Error initializing auth:', error);
          setOverviewLoading(false);
          setSummaryLoading(false);
        }
      } else {
        setOverviewLoading(false);
        setSummaryLoading(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/stats`, { headers });
      setStats(response.data);
    } catch (error: any) {
      const status = error?.response?.status;
      const is500 = status === 500;
      message.error(
        is500
          ? 'Overview statistics are temporarily unavailable. You can still use other tabs.'
          : 'Failed to load overview statistics'
      );
      console.error('Error fetching stats:', error?.response?.data ?? error);
      setStats({ competitions: 0, athletes: 0, results: 0, events: 0 });
    } finally {
      setOverviewLoading(false);
    }
  };

  // Prefetch Database tab data in background once we have stats (so Database tab is ready when user switches)
  useEffect(() => {
    if (stats == null || dbPrefetch != null) return;
    let cancelled = false;
    getAuthHeaders().then((headers) =>
      Promise.all([
        axios.get(`${API_BASE_URL}/api/countries`, { headers }),
        axios.get(`${API_BASE_URL}/api/disciplines`, { headers }),
        axios.get(`${API_BASE_URL}/api/competitions`, { headers, params: { per_page: 100, page: 1 } }),
      ]).then(([countriesRes, disciplinesRes, compsRes]) => {
        if (!cancelled) {
          setDbPrefetch({
            stats,
            countries: countriesRes.data?.countries ?? [],
            disciplines: disciplinesRes.data?.disciplines ?? [],
            competitions: compsRes.data?.competitions ?? [],
          });
        }
      }).catch((err) => !cancelled && console.error('Error prefetching database tab:', err))
    );
    return () => { cancelled = true; };
  }, [stats, dbPrefetch]);

  const fetchMajorGamesBenchmarks = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/singapore/major-games-benchmarks`, { headers });
      setSingaporeStats(prev => ({
        ...prev,
        major_games_benchmarks: response.data,
      } as SingaporeStats));
    } catch (error) {
      console.error('Error fetching major games benchmarks:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <GlobalOutlined />
          {' '}
          SEA Overview
        </span>
      ),
      children: (
        <div>
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {overviewLoading && stats == null ? (
              <Col span={24} style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin size="large" tip="Loading overview..." />
              </Col>
            ) : (
              <>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ height: 'auto', minHeight: '100px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <Statistic
                      title={<span style={{ fontSize: isMobile ? '12px' : '14px', wordBreak: 'break-word' }}>Total Competitions</span>}
                      value={stats?.competitions ?? 0}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ color: 'var(--bright-sky)', fontSize: isMobile ? '20px' : '24px', wordBreak: 'break-all' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ height: 'auto', minHeight: '100px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <Statistic
                      title={<span style={{ fontSize: isMobile ? '12px' : '14px', wordBreak: 'break-word' }}>Total Athletes</span>}
                      value={stats?.athletes ?? 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: 'var(--blue-bell)', fontSize: isMobile ? '20px' : '24px', wordBreak: 'break-all' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ height: 'auto', minHeight: '100px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <Statistic
                      title={<span style={{ fontSize: isMobile ? '12px' : '14px', wordBreak: 'break-word' }}>Total Results</span>}
                      value={stats?.results ?? 0}
                      prefix={<BarChartOutlined />}
                      valueStyle={{ color: 'var(--black)', fontSize: isMobile ? '20px' : '24px', wordBreak: 'break-all' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ height: 'auto', minHeight: '100px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                    <Statistic
                      title={<span style={{ fontSize: isMobile ? '12px' : '14px', wordBreak: 'break-word' }}>Total Events</span>}
                      value={stats?.events ?? 0}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ color: 'var(--racing-red)', fontSize: isMobile ? '20px' : '24px', wordBreak: 'break-all' }}
                    />
                  </Card>
                </Col>
              </>
            )}
          </Row>

          <AnalyticsCharts />
        </div>
      ),
    },
    {
      key: 'singapore',
      label: <span>🇸🇬 Asian Games Analysis</span>,
      children: (
        <>
          <SingaporeSummary
            data={singaporeStats}
            benchmarkLoading={summaryLoading}
          />
        </>
      ),
    },
    {
      key: 'database',
      label: <span>Database Explorer</span>,
      children: <DatabaseStatsTab prefetched={dbPrefetch} />,
    },
  ];

  return (
    <div style={{ padding: window.innerWidth < 768 ? '8px' : '0' }}>
      <Title level={2} style={{ fontSize: isMobile ? '20px' : '28px' }}>
        Southeast Asia Performance Dashboard
      </Title>
      <Title 
        level={4} 
        type="secondary"
        style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '16px' : '24px' }}
      >
        Rolling 12-month marks and Asian/Commonwealth qualifying insights
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'large'}
        style={{ marginTop: isMobile ? '16px' : '24px' }}
      />
    </div>
  );
};

export default Dashboard;
